import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { PdfButton } from './PdfButton';
import { exportEksikliklerPdf, exportTaramaPdf } from '../../utils/ekipmanZekasiPdf';
import { ShieldAlert, RefreshCw, Loader2, Repeat, Radar, Play, Clock, CheckCircle2, XCircle, PauseCircle, KeyRound } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Kapanmamış eksiklikler (raporlar arası izlenen) */
export function EkipmanZekasiEksiklikler() {
  const [data, setData] = useState(null);
  const [kategori, setKategori] = useState('');
  const [tekrar, setTekrar] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/ai/deficiencies`, { params: { kategori: kategori || undefined, tekrar: tekrar ? 1 : 0 }, withCredentials: true });
      setData(r.data);
    } catch (e) { toast.error('Eksiklikler yüklenemedi'); }
    finally { setLoading(false); }
  }, [kategori, tekrar]);
  useEffect(() => { load(); }, [load]);

  const sync = async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/ai/deficiencies/sync`, {}, { withCredentials: true });
      toast.success(`${r.data.islenen} ekipmanın raporları tarandı`);
      await load();
    } catch (e) { toast.error('Tarama başarısız'); }
    finally { setLoading(false); }
  };

  const ozet = data?.ozet || {};
  return (
    <div className="rounded-lg border border-gray-800 bg-[#1A2332] p-4 space-y-3" data-testid="ez-eksiklikler">
      <div className="flex flex-wrap items-center gap-2">
        <ShieldAlert className="w-4 h-4 text-amber-400" />
        <span className="text-sm font-semibold text-white">Kapanmamış Eksiklikler</span>
        <Badge className="bg-gray-700 text-white text-[10px]">{ozet.acik ?? 0} açık</Badge>
        <Badge className="bg-red-800 text-white text-[10px]">{ozet.guvenlik ?? 0} güvenlik</Badge>
        <Badge className="bg-orange-700 text-white text-[10px]">{ozet.tekrar_eden ?? 0} tekrar eden</Badge>
        <div className="ml-auto flex items-center gap-1.5">
          {[['', 'Tümü'], ['guvenlik', 'Güvenlik'], ['diger', 'Diğer']].map(([k, l]) => (
            <button key={k} onClick={() => setKategori(k)} className={`text-[10px] px-2 py-0.5 rounded border ${kategori === k ? 'border-[#E67E22] text-[#E67E22]' : 'border-gray-700 text-gray-400'}`} data-testid={`ez-eks-kat-${k || 'tumu'}`}>{l}</button>
          ))}
          <button onClick={() => setTekrar(!tekrar)} className={`text-[10px] px-2 py-0.5 rounded border flex items-center gap-1 ${tekrar ? 'border-orange-500 text-orange-300' : 'border-gray-700 text-gray-400'}`} data-testid="ez-eks-tekrar"><Repeat className="w-3 h-3" /> Sadece tekrar eden</button>
          <Button size="sm" variant="ghost" onClick={sync} disabled={loading} className="h-7 text-gray-400 hover:text-white" title="Raporlardan yeniden türet" data-testid="ez-eks-sync"><RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} /></Button>
          <PdfButton testId="ez-eks-pdf" disabled={loading || !(data?.items || []).length}
            onExport={(pageSize) => exportEksikliklerPdf({ items: data.items, ozet, filtreOzeti: [kategori === 'guvenlik' ? 'Kategori: Güvenlik' : kategori === 'diger' ? 'Kategori: Diğer' : 'Tüm kategoriler', tekrar && 'Sadece tekrar eden'].filter(Boolean).join(' · '), pageSize })} />
        </div>
      </div>
      <p className="text-[11px] text-gray-500">Periyodik kontrol raporlarındaki eksiklikler rapordan rapora izlenir; bir sonraki raporda hâlâ geçen eksiklik "tekrar eden" sayılır ve Bildirimler'e düşer. LLM gerektirmez.</p>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead><tr className="text-gray-400 border-b border-gray-700 text-left">
            <th className="py-2 pr-3">Kategori</th><th className="py-2 pr-3">Eksiklik</th><th className="py-2 pr-3">Ekipman</th><th className="py-2 pr-3">Bina</th><th className="py-2 pr-3">İlk Görülme</th><th className="py-2 pr-3">Rapor</th><th className="py-2">Açık Gün</th>
          </tr></thead>
          <tbody>
            {(data?.items || []).length === 0 && <tr><td colSpan={7} className="py-6 text-center text-gray-500">{loading ? 'Yükleniyor…' : 'Açık eksiklik yok — filo temiz.'}</td></tr>}
            {(data?.items || []).map(d => (
              <tr key={d.id} className="border-b border-gray-800" data-testid={`ez-eks-${d.id}`}>
                <td className="py-2 pr-3"><Badge className={`text-[10px] ${d.kategori === 'guvenlik' ? 'bg-red-700' : 'bg-gray-600'} text-white`}>{d.kategori === 'guvenlik' ? 'Güvenlik' : 'Diğer'}</Badge></td>
                <td className="py-2 pr-3 text-gray-200 max-w-md">{d.text}</td>
                <td className="py-2 pr-3 text-gray-300">{d.ekipman?.alt_tip || d.ekipman?.tip} <span className="font-mono text-gray-500">{d.ekipman?.seri_no}</span></td>
                <td className="py-2 pr-3 text-gray-400">{d.bina_adi}</td>
                <td className="py-2 pr-3 text-gray-400">{d.ilk_gorulme}</td>
                <td className="py-2 pr-3">{d.rapor_sayisi >= 2 ? <Badge className="bg-orange-700 text-white text-[10px]">{d.rapor_sayisi}× tekrar</Badge> : <span className="text-gray-500">1</span>}</td>
                <td className={`py-2 font-semibold ${d.acik_gun > 90 ? 'text-red-400' : d.acik_gun > 30 ? 'text-orange-400' : 'text-gray-300'}`}>{d.acik_gun ?? '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const DURUM = {
  tamamlandi: { l: 'Tamamlandı', c: 'bg-emerald-700', I: CheckCircle2 }, calisiyor: { l: 'Çalışıyor', c: 'bg-blue-700', I: Loader2 }, hazirlaniyor: { l: 'Hazırlanıyor', c: 'bg-blue-800', I: Loader2 },
  ertelendi: { l: 'Ertelendi (limit)', c: 'bg-amber-600', I: PauseCircle }, anahtar_yok: { l: 'Anahtar yok', c: 'bg-red-800', I: KeyRound }, anahtar_hata: { l: 'Anahtar hatası', c: 'bg-red-800', I: XCircle }, hata: { l: 'Hata', c: 'bg-red-800', I: XCircle },
};

/** Proaktif filo taraması durumu ve geçmişi */
export function EkipmanZekasiTarama({ isAdmin }) {
  const [data, setData] = useState(null);
  const [starting, setStarting] = useState(false);

  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/ai/fleet-scans`, { withCredentials: true }); setData(r.data); } catch (e) { /* ignore */ }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  const start = async () => {
    if (!window.confirm(`Filo taraması başlatılsın mı? Verisi değişen veya 90 günden eski analizli ekipmanlar SİZİN anahtarınızla (${data?.provider || 'Profil'} · ${data?.model || '-'}) analiz edilir (Anthropic → Batch API, %50 maliyet).`)) return;
    setStarting(true);
    try {
      await axios.post(`${API}/ai/fleet-scan`, {}, { withCredentials: true });
      toast.success('Filo taraması arka planda başlatıldı');
      setTimeout(load, 2500);
    } catch (e) { toast.error(e.response?.data?.detail || 'Başlatılamadı'); }
    finally { setStarting(false); }
  };

  return (
    <div className="rounded-lg border border-gray-800 bg-[#1A2332] p-4 space-y-3" data-testid="ez-tarama">
      <div className="flex flex-wrap items-center gap-2">
        <Radar className="w-4 h-4 text-cyan-400" />
        <span className="text-sm font-semibold text-white">Filo Taraması</span>
        <Badge className="bg-gray-700 text-white text-[10px]">Yalnızca manuel</Badge>
        {data && !data.has_key && <Badge className="bg-red-900 text-red-200 text-[10px] flex items-center gap-1" data-testid="ez-tarama-anahtar-yok"><KeyRound className="w-3 h-3" /> Anahtarınız yok — Profil › Yapay Zekâ</Badge>}
        {data?.has_key && <Badge className="bg-gray-800 text-gray-300 text-[10px] flex items-center gap-1" data-testid="ez-tarama-anahtar"><KeyRound className="w-3 h-3" /> {data.provider} · {data.model}</Badge>}
        {data?.running && <Badge className="bg-blue-700 text-white text-[10px] flex items-center gap-1"><Loader2 className="w-3 h-3 animate-spin" /> Tarama sürüyor</Badge>}
        <div className="ml-auto flex items-center gap-2 text-[11px] text-gray-500">
          <Clock className="w-3 h-3" /> Son: {data?.last_fleet_scan_at ? new Date(data.last_fleet_scan_at).toLocaleString('tr-TR') : 'henüz yok'}
          {isAdmin && <Button size="sm" onClick={start} disabled={starting || data?.running || !data?.has_key} className="h-7 bg-cyan-700 hover:bg-cyan-600 text-white text-[11px]" data-testid="ez-tarama-baslat"><Play className="w-3 h-3 mr-1" /> Şimdi Tara</Button>}
          <PdfButton testId="ez-tarama-pdf" disabled={!(data?.items || []).length} onExport={(pageSize) => exportTaramaPdf({ items: data.items, meta: data, pageSize })} />
        </div>
      </div>
      <p className="text-[11px] text-gray-500">Otomatik/zamanlanmış tarama kapalıdır. Tarama yalnızca "Şimdi Tara" ile, tıklayan yöneticinin <b>kendi</b> anahtarıyla koşar; yalnızca verisi değişen (hash) ya da son analizi 90 günü geçen ekipmanlar analiz edilir. Aylık limit aşılırsa tarama ertelenir ve bildirim düşer. Kritik bulgular Bildirimler'e gider.</p>
      <div className="space-y-1.5">
        {(data?.items || []).length === 0 && <p className="text-xs text-gray-600">Henüz tarama yapılmadı.</p>}
        {(data?.items || []).map(s => {
          const m = DURUM[s.durum] || DURUM.hata;
          const I = m.I;
          return (
            <div key={s.id} className="flex flex-wrap items-center gap-2 text-xs border border-gray-800 rounded p-2" data-testid={`ez-tarama-${s.id}`}>
              <Badge className={`${m.c} text-white text-[10px] flex items-center gap-1`}><I className={`w-3 h-3 ${['calisiyor', 'hazirlaniyor'].includes(s.durum) ? 'animate-spin' : ''}`} /> {m.l}</Badge>
              <span className="text-gray-400">{new Date(s.baslangic).toLocaleString('tr-TR')}</span>
              <span className="text-gray-500">· {s.triggered_by === 'scheduler' ? 'zamanlanmış' : 'manuel'}{s.mod === 'batch' ? ' · Batch API' : ''}</span>
              {s.istatistik && <span className="text-gray-400">· {s.istatistik.toplam} ekipman → {s.aday ?? 0} aday ({s.istatistik.yeni} yeni, {s.istatistik.degisen} değişen, {s.istatistik.eski} eski, {s.istatistik.atlanan} atlandı)</span>}
              {s.durum === 'calisiyor' && <span className="text-blue-300">· {s.islenen ?? 0}/{s.aday} işlendi</span>}
              {s.durum === 'tamamlandi' && <span className="text-emerald-300">· {s.basarili} başarılı, {s.hatali} hata{s.ertelenen ? `, ${s.ertelenen} limit ertelemesi` : ''}</span>}
              {s.hata && <span className="text-red-300">· {s.hata}</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}
